import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRegisterInterestApi } from "../../../api/ApiRegisterInterest/ApiRegisterInterest";

interface RegisterInterestProps {
  showSubtext?: boolean;
  buttonLabel?: string;
  buttonClassName?: string;
}

const RegisterInterest: React.FC<RegisterInterestProps> = ({
  showSubtext = true,
  buttonLabel = "Register Interest",
  buttonClassName = "btn-hero-signup",
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const { registerInterest } = useRegisterInterestApi();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = emailRef.current?.value || "";
    const company = (document.getElementById("company") as HTMLInputElement)?.value || "";
    const role = (document.getElementById("role") as HTMLInputElement)?.value || "";
    const comment = (document.getElementById("comment") as HTMLTextAreaElement)?.value || "";

    if (!email || !company || !role) return;

    setLoading(true);
    try {
      const response = await registerInterest({
        email,
        company,
        role,
        comment,
      });

      if (response.status === 202) {
        setShowModal(false);
        setShowThankYou(true);
        setErrorMsg(null);
      } else {
        // Attempt to read error payload
        let message = `Request failed with status ${response.status}`;
        try {
          const data = await response.json();
          if (data?.error) message = data.error;
        } catch {
          /* ignore json parse error */
        }
        setErrorMsg(message);
      }
    } catch (error) {
      console.error("Failed to register interest:", error);
      setErrorMsg("Network error – please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal && emailRef.current) {
      emailRef.current.focus();
    }
  }, [showModal]);

  return (
    <>
      {errorMsg && (
        <div className="alert alert-danger mt-3" role="alert">
          {errorMsg}
        </div>
      )}
      {!showThankYou ? (
        <>
          <button className={buttonClassName} onClick={() => setShowModal(true)}>
            {buttonLabel}
            <i className="bi bi-arrow-right ms-2"></i>
          </button>
          {showSubtext && (
            <p className="register-interest-subtext"><br />VoyCalc is currently onboarding enterprise teams. <br />Register your interest and we'll be in touch!</p>
          )}

          {showModal && createPortal(
            <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
                <h2>Register Interest</h2>
                <form className="modal-form" onSubmit={handleSubmit}>
                  <div className="modal-form-group">
                    <label htmlFor="email" className="modal-form-label text-start">Email *</label>
                    <input ref={emailRef} type="email" id="email" name="email" className="modal-form-input" required />
                  </div>
                  <div className="modal-form-group">
                    <label htmlFor="company" className="modal-form-label text-start">Company *</label>
                    <input type="text" id="company" name="company" className="modal-form-input" required />
                  </div>
                  <div className="modal-form-group">
                    <label htmlFor="role" className="modal-form-label text-start">Role *</label>
                    <input type="text" id="role" name="role" className="modal-form-input" required />
                  </div>
                  <div className="modal-form-group">
                    <label htmlFor="comment" className="modal-form-label text-start">Comment</label>
                    <textarea id="comment" name="comment" className="modal-form-textarea" rows={5} />
                  </div>
                  <p className="modal-form-privacy-note text-start">
                    We use your details to respond to your enquiry and arrange a demonstration.
                    See our <a href="/?page=privacy" target="_blank" rel="noreferrer">Privacy Policy</a> for
                    how we handle personal data.
                  </p>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </form>
              </div>
            </div>,
            document.body
          )}
        </>
      ) : (
        <div className="thank-you-message mt-3">
          <h2>Thank you for registering your interest!</h2>
          <p>We will contact you shortly.</p>
        </div>
      )}
    </>
  );
};

export default RegisterInterest;